import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { useGetAiListeningReloadStatusQuery } from 'src/reactQuery/useGetAiListeningReloadStatusQuery'
import { useReloadAiListeningCampaignMutation } from 'src/reactQuery/useReloadAiListeningCampaignMutation'
import { invalidateAiListeningPromptData } from 'src/reactQuery/utils/aiListeningProfileCache'

type ReloadModalStatus = 'cooldown' | 'error' | 'success'

// Same wording as the pre-redesign details page.
const RELOAD_MODAL_CONTENT: Record<
  ReloadModalStatus,
  { message: string; title: string; type: 'error' | 'success' }
> = {
  cooldown: {
    message:
      'AI Listening prompts can be reloaded once every 24 hours to ensure data integrity and system stability.',
    title: 'Data refresh limit reached',
    type: 'error',
  },
  error: {
    message:
      'Something went wrong while trying to reload the prompt. Please try again later.',
    title: 'Reload failed',
    type: 'error',
  },
  success: {
    message:
      'Every AI model is being asked this prompt again. Fresh results arrive in a few minutes.',
    title: 'Prompt reload started',
    type: 'success',
  },
}

const formatTimeRemaining = (isoDate: string): string => {
  const diff = new Date(isoDate).getTime() - Date.now()
  if (diff <= 0) return '0m'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

// Reload Prompt: the reload call, the reload-status polling while the run is
// pending, the 24 h cooldown and the result modal — carried over from the
// old campaign details page.
export const useReloadPrompt = (promptId: string) => {
  const queryClient = useQueryClient()
  const [isPolling, setIsPolling] = useState(false)
  const [modal, setModal] = useState<{
    isOpen: boolean
    nextReloadTime?: string
    status: ReloadModalStatus
  }>({ isOpen: false, status: 'success' })

  const { data: reloadStatusData } = useGetAiListeningReloadStatusQuery(
    promptId,
    isPolling
  )
  const reloadStatus = reloadStatusData?.data

  const refreshPromptData = useCallback(() => {
    invalidateAiListeningPromptData(queryClient, promptId)
    ;[
      QueryKey.GET_AI_LISTENING_CAMPAIGN_SMART_INSIGHTS,
      QueryKey.GET_AI_LISTENING_CAMPAIGN_RELOAD_HISTORY,
    ].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key, promptId] })
    )
  }, [promptId, queryClient])

  // A run already pending when the page opens is followed too.
  useEffect(() => {
    if (reloadStatus?.status === 'pending') setIsPolling(true)
  }, [reloadStatus?.status])

  // Stop once the run is no longer pending, whatever its outcome.
  useEffect(() => {
    if (
      isPolling &&
      reloadStatus?.status &&
      reloadStatus.status !== 'pending'
    ) {
      setIsPolling(false)
      refreshPromptData()
    }
  }, [isPolling, reloadStatus?.status, refreshPromptData])

  const { isPending: isMutating, mutate } =
    useReloadAiListeningCampaignMutation({
      onCooldown: (nextReloadAvailableAt) =>
        setModal({
          isOpen: true,
          nextReloadTime: formatTimeRemaining(nextReloadAvailableAt),
          status: 'cooldown',
        }),
      onError: () => setModal({ isOpen: true, status: 'error' }),
      onSuccess: () => {
        queryClient.setQueryData(
          [QueryKey.GET_AI_LISTENING_CAMPAIGN_RELOAD_STATUS, promptId],
          { data: { status: 'pending' }, success: true }
        )
        setIsPolling(true)
        setModal({ isOpen: true, status: 'success' })
        refreshPromptData()
      },
    })

  const isPending = reloadStatus?.status === 'pending'
  const nextReloadAt =
    reloadStatus?.status === 'completed'
      ? reloadStatus.nextReloadAvailableAt
      : null

  let label = 'Reload Prompt'
  if (isMutating) label = 'Reloading…'
  else if (isPending) label = 'Analyzing & generating…'
  else if (nextReloadAt)
    label = `Reload in ${formatTimeRemaining(nextReloadAt)}`

  const closeModal = useCallback(
    () => setModal((previous) => ({ ...previous, isOpen: false })),
    []
  )

  const content = RELOAD_MODAL_CONTENT[modal.status]

  return {
    isDisabled: isMutating || isPending || !!nextReloadAt,
    isPending,
    label,
    modal: {
      isOpen: modal.isOpen,
      message:
        modal.status === 'cooldown' && modal.nextReloadTime
          ? `${content.message} Next reload available in: ${modal.nextReloadTime}.`
          : content.message,
      onClose: closeModal,
      title: content.title,
      type: content.type,
    },
    reload: () => promptId && mutate(promptId),
  }
}
